/* @flow */
import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as Scroll from "react-scroll";
import ReactPaginate from "react-paginate";
import "./pagination-styles.css";
import * as S from "./styles";

import { Link } from "../common/Link";
import withData from "../withData";
import Private from "../Private";
import Gettext, { inferLocale } from "../common/Gettext";
import dataLayer from "../common/dataLayer";
import Money from "../Money";

import Header from "../UnitedHeader";
import { InfoIcon } from "../Icons";
import { PaginationArrow } from "./Icons";
import { Icon as HelpIcon } from "../common/SupportLink.js";
import Spinner from "../Spinners/SimpleSpinner";

import ActiveTournaments from "./ActiveTournaments";
import AboutTournaments from "./AboutTournaments";
import ToTopButton from "../Helpdesk/ToTopButton";

import Select from "../HistoryPages/Select";
import Footer from "../Helpdesk/Footer";

import Tournament from "./Api";
import TournamentId from "./TournamentId";

import type { RouterHistory, Location } from "react-router-dom";
import type { Profile } from "../../redux/CommonTypes";
import type { State as ReduxState } from "../../redux/reducer";
import type { MapStateToProps } from "react-redux";
import { useRelevantTournamentInvitations } from "../Bell/Labels";

type Props = {
  profile: Profile,
  history: RouterHistory,
  location: Location,
  changeTournamentId: () => void,
  tournamentId: number,
  showAboutTournaments: boolean
};

type State = {
  activeTab: string,
  isFetching: boolean,
  offset: number
};

type ActiveProps = {
  activeTab: string,
  onClick: () => void,
  apiParams: string
};

function Invitations() {
  let tournaments = useRelevantTournamentInvitations();
  if (tournaments != null && tournaments.length > 0) {
    tournaments.sort((a, b) => {
      if (a.start_date > b.start_date) return 1;
      if (a.start_date < b.start_date) return -1;
      return 0;
    });
    const tournament = tournaments[0];
    // Короткая переменная чтобы избежать переноса строки чтобы избежать проблем
    // с геттекстом 🙄
    let c = (
      <S.InvitedTournamentName>{tournament.title}</S.InvitedTournamentName>
    );

    return (
      <S.Count>
        <S.Text>
          {/* prettier-ignore */}
          <Gettext>You have a special invitation to participate in the tournament named {c}.</Gettext>
        </S.Text>
      </S.Count>
    );
  } else {
    return null;
  }
}

const ActiveTab = (props: ActiveProps) => {
  switch (props.activeTab) {
    case "activeFreeTournaments":
      return <ActiveTournaments type="free" {...props} />;
    case "activePaidTournaments":
      return <ActiveTournaments type="paid" {...props} />;
    case "completedTournaments":
      return <ActiveTournaments isCompleted={true} {...props} />;
    default:
      return null;
  }
};

class Tournaments extends Component<Props, State> {
  state = {
    activeTab: "activeFreeTournaments",
    isFetching: true,
    offset: 0,
    activePage: 0,
    initialLoading: true
  };

  apiParams = type => {
    const limit = this.props.tournamentsPerPage;
    const offset = this.state.offset;
    switch (type) {
      case "activeFreeTournaments":
      case "activePaidTournaments":
        return `/list?limit=100&offset=0&status=1`;
      case "completedTournaments":
        return `/list?limit=${limit}&offset=${offset}&status=2`;
      default:
        return `/list?limit=100&offset=0&status=1`;
    }
  };

  activateTournament = async id => {
    if (window.androidApp) {
      window.androidApp.tournamentTrade(id);
      return;
    }

    let { api } = this.props;

    const activate = await api.fetchEntity(
      `/api/tournaments/activate?id=${id}`
    );

    if (activate !== null) {
      api.fetchEntity(
        `/api/tournaments${this.apiParams(this.state.activeTab)}`
      );
      await api.fetchEntity(`/api/tournaments/stats`);
      this.props.dispatch({
        type: "ENGAGE_TOURNAMENT_MODE"
      });
      this.props.history.push("/trade");
    }
  };

  popupCallback = status => {
    if (status === "success") {
      const { api } = this.props;

      api.fetchEntity(
        `/api/tournaments${this.apiParams(this.state.activeTab)}`
      );
    }
  };

  handleTabClick = tab => {
    if (this.state.activeTab !== tab) {
      this.setState({
        activeTab: tab,
        activePage: 0,
        offset: 0
      });

      dataLayer({
        event: "event-to-ga",
        eventCategory: "Tournaments",
        eventAction: "all-tournaments-page-tab-changed",
        eventLabel: `Пользователь переключился на таб ${tab} на странице всех турниров`
      });
    }
    return null;
  };

  scrollToTop = () => {
    Scroll.animateScroll.scrollToTop({
      duration: 500,
      isDynamic: true
    });
  };

  onPageSizeChange = value => {
    if (value === this.props.tournamentsPerPage) {
      return false;
    }

    const data = this.props.api.readEntity(
      `/api/tournaments${this.apiParams(this.state.activeTab)}`
    );

    if (
      data.value != null &&
      value >= data.value.count &&
      this.props.tournamentsPerPage >= data.value.count
    ) {
      this.props.dispatch({
        type: "UPDATE_SETTINGS",
        payload: { tournamentsPerPage: value }
      });
      this.setState({
        offset: 0,
        activePage: 0
      });
      return false;
    }

    this.props.dispatch({
      type: "UPDATE_SETTINGS",
      payload: { tournamentsPerPage: value }
    });

    this.setState({
      offset: 0,
      activePage: 0
    });
  };

  goToPage = data => {
    const page = data.selected;
    if (page === this.state.activePage) {
      return false;
    }

    this.setState({
      activePage: page,
      offset: this.props.tournamentsPerPage * page
    });
  };

  toggleAbout = () => {
    this.props.dispatch({
      type: "UPDATE_SETTINGS",
      payload: { showAboutTournaments: !this.props.showAboutTournaments }
    });
  };

  handleInitialLoading = async () => {
    await this.props.api.fetchEntity(
      `/api/tournaments${this.apiParams(this.state.activeTab)}`
    );
    this.setState({ initialLoading: false });
  };

  componentDidMount() {
    dataLayer({
      event: "event-to-ga",
      eventCategory: "Tournaments",
      eventAction: "all-tournaments-page-mount",
      eventLabel: "Пользователь перешел на страницу всех турниров"
    });

    this.handleInitialLoading();
    this.props.api.fetchEntity(`/api/tournaments/stats`);
  }

  componentDidUpdate(prevProps, prevState) {
    const data = this.props.api.readEntity(
      `/api/tournaments${this.apiParams(this.state.activeTab)}`
    );
    if (
      data.status !== "Pending" &&
      prevState.activeTab !== this.state.activeTab
    ) {
      this.props.api.fetchEntity(
        `/api/tournaments${this.apiParams(this.state.activeTab)}`
      );
      this.props.api.fetchEntity(`/api/tournaments/stats`);
    }
  }

  render() {
    const { profile, api } = this.props;
    const { activePage, activeTab } = this.state;
    const pageSizeOptions = [
      { label: 10, value: 10 },
      { label: 20, value: 20 },
      { label: 30, value: 30 },
      { label: 40, value: 40 }
    ];
    const data = api.readEntity(`/api/tournaments${this.apiParams(activeTab)}`);

    const isFetching = this.state.initialLoading || data.value == null;

    const limit = this.props.tournamentsPerPage;
    const showPagination = data.value != null && data.value.count > limit;
    // считаем высоту плиток турниров, что бы красиво посередине рисовать спиннер
    const spinnerHeight =
      !isFetching &&
      (showPagination
        ? Math.ceil(limit / 2) * 168
        : Math.ceil(data.value.count / 2) * 168);

    const stats = api.readEntity(`/api/tournaments/stats`);

    const locale = inferLocale(this.props) || "en";

    const participationCount =
      stats.value != null && stats.value.participation_count > 0 ? (
        <S.Count>
          <S.Text>
            <Gettext>
              You participated in
              <S.Numbers>
                {" "}
                <span>{stats.value.participation_count}</span>{" "}
              </S.Numbers>
              tournaments.
            </Gettext>
          </S.Text>
        </S.Count>
      ) : (
        <S.Count>
          <S.Text>
            <Gettext>You did not participate in any tournaments yet.</Gettext>
          </S.Text>
        </S.Count>
      );

    const accumulatedProfitCount =
      stats.value != null && stats.value.accumulated_profit !== "0.00" ? (
        <S.Count>
          <S.Text>
            <Gettext>
              You have earned
              <S.Numbers>
                {" "}
                <Money>{stats.value.accumulated_profit}</Money>{" "}
              </S.Numbers>
              in tournaments.
            </Gettext>
          </S.Text>
        </S.Count>
      ) : (
        <S.Count>
          <S.Text>
            <Gettext>You are not the winner yet.</Gettext>
          </S.Text>
        </S.Count>
      );

    const invitationCount = <Invitations />;

    return (
      <React.Fragment>
        <Header
          showMainHeader={false}
          location={this.props.location}
          history={this.props.history}
        />
        <S.PageContainer>
          <S.Title>
            <Gettext>Tournaments</Gettext>
          </S.Title>

          <S.Container>
            <S.UserInfo>
              <S.AccountInfo>
                <S.UserEmail>
                  <Link to="/profile">{profile.email}</Link>
                </S.UserEmail>
                <S.UserId>
                  <InfoIcon />
                  id: <Link to="/profile">{profile.id}</Link>
                </S.UserId>
              </S.AccountInfo>
              {stats.value != null && (
                <S.Counts>
                  {participationCount}
                  {accumulatedProfitCount}
                  {invitationCount}
                </S.Counts>
              )}
            </S.UserInfo>

            <S.Tabs>
              <S.Tab
                active={this.state.activeTab === "activeFreeTournaments"}
                onClick={() => this.handleTabClick("activeFreeTournaments")}
              >
                <Gettext>Active free</Gettext>
              </S.Tab>
              <S.Tab
                active={this.state.activeTab === "activePaidTournaments"}
                onClick={() => this.handleTabClick("activePaidTournaments")}
              >
                <Gettext>Active paid</Gettext>
              </S.Tab>
              <S.Tab
                active={this.state.activeTab === "completedTournaments"}
                onClick={() => this.handleTabClick("completedTournaments")}
              >
                <Gettext>Finished</Gettext>
              </S.Tab>
            </S.Tabs>

            <S.Tournaments>
              {!isFetching ? (
                <ActiveTab
                  activeTab={this.state.activeTab}
                  popupCallback={this.popupCallback}
                  {...this.props}
                  data={data}
                  onActivateTournament={this.activateTournament}
                  onActiveTradeClick={this.activateTournament}
                  onClick={this.handlePopupOpening}
                  locale={locale}
                  apiParams={this.apiParams(this.state.activeTab)}
                />
              ) : (
                <S.SpinnerContainer spinnerHeight={spinnerHeight}>
                  <Spinner accent />
                </S.SpinnerContainer>
              )}
            </S.Tournaments>
            <S.Bottom activeTab={activeTab}>
              {activeTab === "completedTournaments" && (
                <React.Fragment>
                  <S.PaginationSelect>
                    <S.SelectText>
                      <Gettext>Rows per page</Gettext>:
                    </S.SelectText>
                    <Select
                      name="page-rows-select"
                      options={pageSizeOptions}
                      value={{ value: limit, label: limit }}
                      onChange={e => {
                        this.onPageSizeChange(Number(e.value));
                      }}
                    />
                  </S.PaginationSelect>
                  {showPagination && (
                    <ReactPaginate
                      initialPage={activePage}
                      forcePage={activePage}
                      previousLabel={<PaginationArrow />}
                      nextLabel={<PaginationArrow />}
                      breakLabel={<span>...</span>}
                      breakClassName={"break-me"}
                      pageCount={Math.ceil(
                        data.value && data.value.count / limit
                      )}
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={5}
                      onPageChange={this.goToPage}
                      containerClassName={"pagination"}
                      subContainerClassName={"pages pagination"}
                      activeClassName={"active"}
                    />
                  )}
                </React.Fragment>
              )}

              <S.HelpContainer>
                <S.HelpLink to="helpdesk/faq" target="_blank">
                  <Gettext>Need any help?</Gettext>
                </S.HelpLink>
                <HelpIcon />
              </S.HelpContainer>
            </S.Bottom>
            {this.state.activeTab !== "completedTournaments" && (
              <React.Fragment>
                <S.AboutToggle
                  onClick={this.toggleAbout}
                  isOpen={this.props.showAboutTournaments}
                >
                  <S.AboutTitle>
                    <Gettext>About tournaments</Gettext>
                  </S.AboutTitle>
                </S.AboutToggle>
                {this.props.showAboutTournaments && (
                  <AboutTournaments
                    locale={locale}
                    isOpen={this.props.showAboutTournaments}
                  />
                )}
              </React.Fragment>
            )}
          </S.Container>
          <Footer />
          <ToTopButton onClick={this.scrollToTop} hideTrade={true} />
        </S.PageContainer>
      </React.Fragment>
    );
  }
}

const T = (props: {}) => (
  <Tournament.Consumer>
    {api => (
      <TournamentId.Consumer>
        {contextId => <Tournaments {...props} {...contextId} api={api} />}
      </TournamentId.Consumer>
    )}
  </Tournament.Consumer>
);

export default Private(
  withRouter(
    withData({ required: ["serverData"], optional: ["profile"] })(
      connect(
        ((state: ReduxState) => ({
          time: state.time || Date.now(),
          tournamentsPerPage: state.settings.tournamentsPerPage || 10,
          showAboutTournaments:
            state.settings.showAboutTournaments != null
              ? state.settings.showAboutTournaments
              : true
        }): MapStateToProps<*, *, *>)
      )(T)
    )
  )
);



// WEBPACK FOOTER //
// ./src/components/Tournaments/Tournaments.js